import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Markdown } from "@/components/Markdown";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowRight } from "lucide-react";

export default function PlanPage() {
  const { token } = useParams();
  const [plan, setPlan] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | error

  useEffect(() => {
    document.title = "Your Growth Plan";
    api.get(`/plan/${token}`)
      .then(({ data }) => { setPlan(data); setState("ready"); })
      .catch(() => setState("error"));
  }, [token]);

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center" data-testid="plan-loading">
        <Loader2 className="h-7 w-7 animate-spin text-emerald-400" />
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-center px-6" data-testid="plan-notfound">
        <h1 className="font-display text-2xl font-bold text-white">Plan not found</h1>
        <p className="mt-2 text-slate-400 text-sm">This growth plan link is invalid or has expired.</p>
      </div>
    );
  }

  const firstName = (plan.owner_name || "").trim().split(/\s+/)[0];
  const heading = firstName ? `${firstName}'s Growth Plan` : "Your Growth Plan";

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="plan-page">
      <div className="mx-auto max-w-2xl px-5 py-10 sm:py-14">
        <h1 className="font-display text-3xl sm:text-4xl font-bold leading-tight" data-testid="plan-heading">{heading}</h1>
        {plan.goal ? (
          <p className="mt-2 text-slate-400 text-sm sm:text-base">Your 12-month goal: {plan.goal}</p>
        ) : null}

        {/* Plan body */}
        <div className="mt-8 rounded-2xl border border-slate-800 bg-slate-900/60 p-5 sm:p-7">
          <Markdown className="text-[15px] leading-relaxed" data-testid="plan-content">{plan.content_md}</Markdown>
        </div>

        {/* CTA */}
        <div className="mt-8 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6 text-center">
          <h2 className="text-lg font-semibold text-white">Ready to put this plan to work?</h2>
          <p className="mt-1.5 text-sm text-slate-300">See exactly how the system does it for you — or book a quick call with an expert.</p>
          <a href={plan.program_url} target="_blank" rel="noopener noreferrer" data-testid="plan-cta">
            <Button className="mt-4 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full px-6 h-11">
              Show me how it works <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </a>
        </div>

        <p className="mt-8 text-center text-[11px] text-slate-600">
          These numbers are averages and examples, not guarantees — your results will vary.
        </p>
      </div>
    </div>
  );
}
